import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { User, Project, AuthState, LoginCredentials, SignupCredentials } from '@/types/auth';
import * as authApi from '@/api/auth';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; requires_2fa?: boolean; error?: string }>;
  verify2FA: (code: string) => Promise<{ success: boolean; error?: string }>;
  signup: (credentials: SignupCredentials) => Promise<{ success: boolean; error?: string; errors?: Record<string, string>; requires_challenge?: boolean; challenge_question?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.fetchCurrentUser();
      if (response.authenticated && response.user) {
        setUser(response.user);
        setProjects(response.projects || []);
        setIsAuthenticated(true);

        // Force redirects based on user state for new users
        // But don't redirect if already on the target page (prevents infinite loops)
        if (!response.user.email_confirmed) {
          if (location.pathname !== '/auth/resend-confirmation') {
            navigate('/auth/resend-confirmation');
          }
          return;
        }

        if (!response.user.onboarding_completed) {
          if (!location.pathname.startsWith('/onboarding')) {
            navigate('/onboarding');
          }
          return;
        }

        // User is fully set up - allow normal navigation
      } else {
        setUser(null);
        setProjects([]);
        setIsAuthenticated(false);
      }
    } catch {
      setUser(null);
      setProjects([]);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    // Only call refreshUser on initial mount when we don't know the auth state yet
    // Don't call it when isAuthenticated changes to false (like after logout)
    if (isLoading) {
      refreshUser();
    }
  }, [refreshUser, isLoading]);

  const login = async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials);

    if (response.success) {
      if (response.requires_2fa) {
        return { success: true, requires_2fa: true };
      }

      if (response.requires_email_confirmation) {
        navigate('/auth/resend-confirmation');
        return { success: true };
      }

      // Login successful - use the user data from the response instead of making another API call
      // This avoids potential race conditions with session cookie availability
      if (response.user) {
        // Use flushSync to ensure auth state updates are synchronous before navigation
        // This prevents race conditions where ProtectedRoute checks happen before state is updated
        flushSync(() => {
          setUser(response.user!);
          setProjects(response.projects || []);
          setIsAuthenticated(true);
        });

        // Force redirects based on user state for new users
        if (!response.user.email_confirmed) {
          // Email not confirmed - redirect to confirmation page
          navigate('/auth/resend-confirmation');
          return { success: true };
        }

        if (!response.user.onboarding_completed) {
          // Email confirmed but onboarding not completed - redirect to onboarding
          navigate('/onboarding');
          return { success: true };
        }

        // Navigate to redirect URL or dashboard
        if (response.redirect) {
          navigate(response.redirect);
        }
      } else {
        // Fallback to refreshUser if no user data in response
        await refreshUser();

        // Navigate to redirect URL or dashboard
        if (response.redirect) {
          navigate(response.redirect);
        }
      }

      return { success: true };
    }

    return { success: false, error: response.error };
  };

  const verify2FA = async (code: string) => {
    const response = await authApi.verify2FA(code);

    if (response.success) {
      if (response.requires_email_confirmation) {
        navigate('/auth/resend-confirmation');
        return { success: true };
      }

      // 2FA successful - use the user data from the response instead of making another API call
      // This avoids potential race conditions with session cookie availability
      if (response.user) {
        // Use flushSync to ensure auth state updates are synchronous before navigation
        flushSync(() => {
          setUser(response.user!);
          setProjects(response.projects || []);
          setIsAuthenticated(true);
        });

        // Force redirects based on user state for new users
        if (!response.user.email_confirmed) {
          // Email not confirmed - redirect to confirmation page
          navigate('/auth/resend-confirmation');
          return { success: true };
        }

        if (!response.user.onboarding_completed) {
          // Email confirmed but onboarding not completed - redirect to onboarding
          navigate('/onboarding');
          return { success: true };
        }

        // Navigate to redirect URL or dashboard
        if (response.redirect) {
          navigate(response.redirect);
        }
      } else {
        // Fallback to refreshUser if no user data in response
        await refreshUser();
      }

      return { success: true };
    }

    return { success: false, error: response.error };
  };

  const signup = async (credentials: SignupCredentials) => {
    const response = await authApi.signup(credentials);

    if (response.success) {
      // Signup successful - use the user data from the response instead of making another API call
      // This avoids potential race conditions with session cookie availability
      if (response.user) {
        // Use flushSync to ensure auth state updates are synchronous before navigation
        // Also set isLoading to false to prevent useEffect from calling refreshUser
        flushSync(() => {
          setUser(response.user!);
          setProjects(response.projects || []);
          setIsAuthenticated(true);
          setIsLoading(false);
        });

        // Check for email confirmation requirement and navigate accordingly
        if (response.requires_email_confirmation) {
          navigate('/auth/resend-confirmation');
          return { success: true };
        }

        // If backend provides a redirect URL, trust it (handles invite signups correctly)
        if (response.redirect) {
          navigate(response.redirect);
          return { success: true };
        }

        // Fallback checks for user state
        if (!response.user.email_confirmed) {
          navigate('/auth/resend-confirmation');
          return { success: true };
        }

        if (!response.user.onboarding_completed) {
          navigate('/onboarding');
          return { success: true };
        }
      } else {
        // Fallback to refreshUser if no user data in response
        await refreshUser();

        // Check for email confirmation requirement AFTER refreshUser
        if (response.requires_email_confirmation) {
          navigate('/auth/resend-confirmation');
          return { success: true };
        }

        // Navigate to redirect URL or onboarding
        if (response.redirect) {
          navigate(response.redirect);
        }
      }

      return { success: true };
    }

    return {
      success: false,
      error: response.error,
      errors: response.errors,
      requires_challenge: response.requires_challenge,
      challenge_question: response.challenge_question,
    };
  };

  const logout = async () => {
    await authApi.logout();

    // Clear React Query cache to prevent data from previous user showing
    queryClient.clear();

    // Use flushSync to ensure state updates are synchronous before navigation
    // This prevents GuestRoute from seeing stale authentication state
    flushSync(() => {
      setUser(null);
      setProjects([]);
      setIsAuthenticated(false);
    });

    navigate('/auth/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        projects,
        isAuthenticated,
        isLoading,
        login,
        verify2FA,
        signup,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
